import { useLocalStorageState, useSessionStorageState } from 'ahooks';
import { useEffect, useMemo, useState } from 'react';
import { HOME_PAGE } from '@/app/constants';
import { useAdsDialog } from '@/app/hooks';
import type { UserInfo } from '@/app/types';
import { getTodayDateString } from '@/app/utils';

interface GoogleAdsSessionState {
  visited: boolean;
}

interface DailySessionCount {
  date: string;
  count: number;
}

interface UseGoogleAdsOptions {
  pathname: string;
  isUserInfoInitialized: boolean;
  userInfo: UserInfo | null;
  sessionDialogLocked: boolean;
}

export const useGoogleAds = ({
  pathname,
  isUserInfoInitialized,
  userInfo,
  sessionDialogLocked,
}: UseGoogleAdsOptions) => {
  const [today, setToday] = useState('');
  // 是否有资格显示广告（异步检查结果）
  const [isEligible, setIsEligible] = useState<boolean | null>(null);
  const { checkEligible } = useAdsDialog();
  const [dailySessionCount, setDailySessionCount] = useLocalStorageState<DailySessionCount>('DAILY_SESSION_COUNT', {
    defaultValue: { date: '', count: 0 },
  });

  const [sessionState, setSessionState] = useSessionStorageState<GoogleAdsSessionState>('GOOGLE_ADS_SESSION_STATE', {
    defaultValue: { visited: false },
  });

  // 检查是否应该显示GoogleAds弹窗（基础条件）
  const shouldCheckGoogleAds = useMemo(() => {
    if (!today) {
      return false;
    }
    // 如果当前会话已访问过，则不显示
    if (sessionState?.visited) {
      return false;
    }
    // 如果当前会话未访问过，则检查每日访问次数
    const sessionCount = dailySessionCount?.date === today ? dailySessionCount.count : 0;
    return sessionCount >= 0 && sessionCount < 3 && !sessionDialogLocked && pathname.includes(HOME_PAGE);
  }, [dailySessionCount, sessionState, pathname, today]);

  useEffect(() => {
    setToday(getTodayDateString());
  }, []);

  // 更新每日访问次数
  useEffect(() => {
    if (!today) {
      return;
    }
    const isNewDay = dailySessionCount?.date !== today;
    if (isNewDay && sessionState?.visited) {
      setSessionState({
        visited: false,
      });
    }
  }, [today]);

  // 异步检查广告资格
  useEffect(() => {
    if (!isUserInfoInitialized || !userInfo) {
      setIsEligible(null);
      return;
    }

    if (!shouldCheckGoogleAds) {
      setIsEligible(false);
      return;
    }

    const checkAdsEligibility = async () => {
      try {
        // 直接使用，类型匹配
        const eligible = await checkEligible('watch_ad_credit');
        setIsEligible(eligible);
      } catch {
        setIsEligible(false);
      }
    };

    checkAdsEligibility();
  }, [isUserInfoInitialized, userInfo, shouldCheckGoogleAds]);

  const markAdsShown = () => {
    setSessionState(() => ({
      visited: true,
    }));

    const today = getTodayDateString();
    const isNewDay = dailySessionCount?.date !== today;
    setDailySessionCount({
      date: today,
      count: isNewDay ? 1 : (dailySessionCount?.count || 0) + 1,
    });
  };

  return {
    adsSessionStateVisited: sessionState?.visited,
    shouldCheckGoogleAds,
    isEligible,
    dailySessionCount,
    markAdsShown,
  };
};
